/* Studios1996 widgets. Native Shopify product and cart components remain in charge. */
(() => {
  const define = (name, component) => {
    if (!customElements.get(name)) customElements.define(name, component);
  };
  class MarineMarquee extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.visibleVideos = new Set();
      this.userPaused = false;
      this.hoverPaused = false;
      this.focusPaused = false;
      this.continuous = this.hasAttribute('data-continuous');
      this.button = this.querySelector('[data-motion-toggle]');
      this.button?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.button.setAttribute('aria-pressed', String(this.userPaused));
        this.button.setAttribute('aria-label', this.userPaused ? this.button.dataset.resume : this.button.dataset.pause);
        this.updatePlayback();
      }, { signal });
      this.addEventListener('pointerenter', () => { this.hoverPaused = !this.continuous; this.updatePlayback(); }, { signal });
      this.addEventListener('pointerleave', () => { this.hoverPaused = false; this.updatePlayback(); }, { signal });
      const track = this.querySelector('[data-marquee-track]');
      track?.addEventListener('focusin', () => { this.focusPaused = !this.continuous; this.updatePlayback(); }, { signal });
      track?.addEventListener('focusout', event => {
        if (!track.contains(event.relatedTarget)) { this.focusPaused = false; this.updatePlayback(); }
      }, { signal });
      document.addEventListener('visibilitychange', () => this.updatePlayback(), { signal });
      this.reducedListener = () => this.updatePlayback();
      this.reduced.addEventListener('change', this.reducedListener);
      const videos = this.querySelectorAll('video');
      videos.forEach(video => {
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.addEventListener('canplay', () => this.updatePlayback(), { signal });
      });
      document.addEventListener('pointerdown', () => this.updatePlayback(), { once:true, signal });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) this.visibleVideos.add(entry.target);
            else this.visibleVideos.delete(entry.target);
          });
          this.updatePlayback();
        }, { threshold: 0.1 });
        videos.forEach(video => this.observer.observe(video));
      } else {
        videos.forEach(video => this.visibleVideos.add(video));
      }
      this.equalize = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => {
          if (!this.isConnected) return;
          const cards = [...this.querySelectorAll('.tp-card')];
          cards.forEach(card => { card.style.height = ''; });
          const height = Math.max(0, ...cards.map(card => card.offsetHeight));
          cards.forEach(card => { if (height) card.style.height = `${height}px`; });
        });
      };
      window.addEventListener('resize', this.equalize, { signal });
      this.querySelectorAll('img').forEach(img => img.addEventListener('load', this.equalize, { signal }));
      if (document.fonts?.ready) document.fonts.ready.then(() => { if (this.isConnected) this.equalize(); });
      this.equalize();
      this.updatePlayback();
    }
    updatePlayback() {
      const paused = this.userPaused || this.hoverPaused || this.focusPaused || this.reduced.matches || document.hidden;
      this.toggleAttribute('data-paused', paused);
      this.querySelectorAll('video').forEach(video => {
        const visible = this.visibleVideos.has(video);
        // Load the first frame even when motion is paused or reduced.
        if (visible && !video.getAttribute('src') && video.dataset.src) {
          video.preload = 'auto';
          video.src = video.dataset.src;
          video.load();
        }
        if (visible && !paused && video.paused) video.play()?.catch(() => {});
        else if (!visible || paused) video.pause();
        video.controls = this.reduced.matches && !video.closest('[aria-hidden="true"]');
      });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.reduced?.removeEventListener('change', this.reducedListener);
      this.querySelectorAll('video').forEach(video => video.pause());
      cancelAnimationFrame(this.frame);
    }
  }
  define('ml-marquee', MarineMarquee);

  class MarineColorPicker extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const native = this.querySelector('variant-selects');
      const index = Number(this.dataset.colorIndex);
      if (!native || index < 0) return;
      this.native = native;
      this.group = native.querySelectorAll('.product-form__input')[index];
      if (!this.group) return;
      this.group.classList.add('ml-native-color');
      const thumbs = this.querySelector('[data-color-thumbnails]');
      if (thumbs) thumbs.hidden = false;
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-color-value]');
        if (!button || !this.contains(button)) return;
        const value = button.dataset.colorValue;
        const select = this.group.querySelector('select');
        const radio = [...this.group.querySelectorAll('input[type="radio"]')].find(input => input.value === value);
        const control = select || radio;
        if (!control || control.disabled) return;
        if (select) select.value = value;
        else radio.checked = true;
        // Change exactly this option. The native component resolves the complete variant.
        control.dispatchEvent(new Event('change', { bubbles: true }));
        this.sync();
      }, { signal });
      native.addEventListener('change', () => this.sync(), { signal });
      this.sync();
    }
    sync() {
      const selected = this.group.querySelector('select')?.value || this.group.querySelector('input:checked')?.value;
      this.querySelectorAll('[data-color-value]').forEach(button => {
        button.setAttribute('aria-pressed', String(button.dataset.colorValue === selected));
      });
      const label = this.querySelector('[data-selected-color]');
      if (label) label.textContent = [...this.querySelectorAll('[data-color-value]')].find(button => button.dataset.colorValue === selected)?.dataset.colorLabel || selected || '';
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; }
  }
  define('ml-color-picker', MarineColorPicker);

  class MarineSizeGuide extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.dialog = this.querySelector('dialog');
      this.opener = this.querySelector('[data-size-open]');
      this.opener?.addEventListener('click', () => this.dialog?.showModal(), { signal });
      this.querySelector('[data-size-close]')?.addEventListener('click', () => this.dialog?.close(), { signal });
      this.dialog?.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      }, { signal });
      this.dialog?.addEventListener('close', () => this.opener?.focus(), { signal });
      this.querySelectorAll('[data-unit]').forEach(button => button.addEventListener('click', () => {
        const inches = button.dataset.unit === 'in';
        this.querySelectorAll('[data-cm]').forEach(cell => {
          const value = Number(cell.dataset.cm);
          if (Number.isFinite(value)) cell.textContent = new Intl.NumberFormat(this.dataset.locale || 'en-US', { maximumFractionDigits: 1 }).format(inches ? value / 2.54 : value);
        });
        this.querySelectorAll('[data-unit]').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
      }, { signal }));
    }
    disconnectedCallback() { this.dialog?.close(); this.controller?.abort(); this.controller = null; }
  }
  define('ml-size-guide', MarineSizeGuide);

  class MarineCartReviews extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.slides = [...this.querySelectorAll('[data-review-slide]')];
      this.index = 0;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-review-step], [data-review-index]');
        if (!button || !this.contains(button)) return;
        this.show(button.dataset.reviewIndex !== undefined ? Number(button.dataset.reviewIndex) : this.index + Number(button.dataset.reviewStep));
        this.restart();
      }, { signal });
      this.addEventListener('pointerenter', () => { this.hoverPaused = true; this.stop(); }, { signal });
      this.addEventListener('pointerleave', () => { this.hoverPaused = false; this.restart(); }, { signal });
      this.addEventListener('focusin', () => this.stop(), { signal });
      this.addEventListener('focusout', event => { if (!this.contains(event.relatedTarget)) this.restart(); }, { signal });
      document.addEventListener('visibilitychange', () => this.restart(), { signal });
      this.reducedListener = () => this.restart();
      this.reduced.addEventListener('change', this.reducedListener);
      this.show(0);
      this.restart();
    }
    show(index) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, n) => { slide.hidden = n !== this.index; });
      this.querySelectorAll('[data-review-index]').forEach((dot, n) => dot.setAttribute('aria-current', String(n === this.index)));
    }
    stop() { clearInterval(this.timer); this.timer = null; }
    restart() {
      this.stop();
      if (this.isConnected && this.slides.length > 1 && !this.reduced.matches && !document.hidden && !this.hoverPaused && !this.contains(document.activeElement)) {
        this.timer = setInterval(() => this.show(this.index + 1), 4500);
      }
    }
    disconnectedCallback() {
      this.stop(); this.controller?.abort(); this.controller = null;
      this.reduced?.removeEventListener('change', this.reducedListener);
    }
  }
  define('ml-cart-reviews', MarineCartReviews);

  class MarineDeliveryTimeline extends HTMLElement {
    connectedCallback() {
      const addBusinessDays = (date, days) => {
        const result = new Date(date);
        for (let remaining = days; remaining > 0;) {
          result.setDate(result.getDate() + 1);
          if (result.getDay() !== 0 && result.getDay() !== 6) remaining--;
        }
        return result;
      };
      const readDays = key => Math.max(0, Number(this.dataset[key]) || 0);
      const start = new Date();
      const pmin = readDays('processingMin');
      const pmax = Math.max(pmin, readDays('processingMax'));
      const tmin = readDays('transitMin');
      const tmax = Math.max(tmin, readDays('transitMax'));
      const fmt = new Intl.DateTimeFormat(this.dataset.locale || 'en-US', { month: 'short', day: 'numeric' });
      const values = [fmt.format(start), `${fmt.format(addBusinessDays(start, pmin))} – ${fmt.format(addBusinessDays(start, pmax))}`, `${fmt.format(addBusinessDays(start, pmin + tmin))} – ${fmt.format(addBusinessDays(start, pmax + tmax))}`];
      this.querySelectorAll('[data-date]').forEach((el, index) => { el.textContent = values[index] || ''; });
    }
  }
  define('ml-delivery-timeline', MarineDeliveryTimeline);
})();
